import { useEffect, useState } from "react";

const scriptUrl = import.meta.env.VITE_INVESTMENT_QUESTIONNAIRE_URL || "";
const scriptApiKey =
	import.meta.env.VITE_INVESTMENT_QUESTIONNAIRE_API_KEY || "";

// SDK global interface
declare global {
	interface Window {
		slsIqApp?: {
			init: (apiKey: string) => void;
			openQuestionnaireForm: (payload: QuestionnairePayload) => void;
		};
	}
}

export type QuestionnairePayload = {
	/** Klientův email - Povinné pouze pro vytvoření nového dotazníku. Při 'onlyFinancialBackground = true' bude ignorováno */
	clientEmail: string;
	/** Id klienta */
	clientId: string;
	/** Celé jméno klienta - Povinné pouze pro vytvoření nového dotazníku. Při 'onlyFinancialBackground = true' bude ignorováno */
	name: string;
	/** Zástupce klienta - Povinné pro 'entityType = po' */
	clientRepresentative?: string;
	/** Client representative date of birth - Povinné pro 'entityType = po' */
	clientRepresentativeDateOfBirth?: Date;
	/** Povinné pouze pro vytvoření nového dotazníku. Při 'onlyFinancialBackground = true' bude ignorováno */
	entityType: "fo" | "fop" | "po";
	/** Namespace určující systém, který vytvoří dotazník */
	namespace: string;
	/** Adresa pro zaslání notifikace o dokončení dotazníku */
	notificationUrl?: string;
	/** Budou zobrazeny pouze otázky na finanční zázemí pro již existující dotazník */
	onlyFinancialBackground?: boolean;
	/** Password for pdf file. For FO please use date of birth in form 'DD.MM.YYYY' and for PO use TIN - Povinné pouze pro vytvoření nového dotazníku. Při 'onlyFinancialBackground = true' bude ignorováno */
	password?: string;
	/** Výdaje na finanční produkty pro finanční zázemí */
	financialProductsExpenditure?: number;
	/** Výdaje za domácnost pro finanční zázemí */
	householdExpenditure?: number;
	/** Finanční rezerva pro finanční zázemí */
	reserve?: number;
};

type QuestionnaireCreatedEvent = {
	detail: {
		uuid: string;
		pdfPreviewLink?: string;
		pdfDownloadLink?: string;
	};
};

type UseInvestmentQuestionnaireSdkOptions = {
	onQuestionnaireCreated?: (event: QuestionnaireCreatedEvent["detail"]) => void;
	onError?: (error: Error) => void;
};

export const useInvestmentQuestionnaireSdk = ({
	onQuestionnaireCreated,
	onError,
}: UseInvestmentQuestionnaireSdkOptions) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	// Check if SDK is configured with required environment variables
	const isConfigured = Boolean(scriptUrl && scriptApiKey);

	useEffect(() => {
		// Don't load SDK if not configured
		if (!isConfigured) {
			const error = new Error(
				"Investment Questionnaire SDK is not configured. Please set VITE_INVESTMENT_QUESTIONNAIRE_URL and VITE_INVESTMENT_QUESTIONNAIRE_API_KEY environment variables.",
			);
			onError?.(error);
			return;
		}

		// Check if SDK is already loaded
		if (window.slsIqApp) {
			setIsLoaded(true);
			return;
		}

		// Check if script is already being loaded
		const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
		if (existingScript) {
			setIsLoading(true);
			existingScript.addEventListener("load", () => {
				setIsLoading(false);
				setIsLoaded(true);
				if (window.slsIqApp) {
					window.slsIqApp.init(scriptApiKey);
				}
			});
			return;
		}

		// Load SDK script
		setIsLoading(true);
		const script = document.createElement("script");
		script.src = scriptUrl;
		script.type = "module";
		script.async = true;

		script.onload = () => {
			setIsLoading(false);
			if (window.slsIqApp) {
				window.slsIqApp.init(scriptApiKey);
				setIsLoaded(true);
			} else {
				const error = new Error("SDK failed to initialize");
				onError?.(error);
			}
		};

		script.onerror = () => {
			setIsLoading(false);
			const error = new Error("Failed to load SDK script");
			onError?.(error);
		};

		document.body.appendChild(script);

		return () => {
			// Don't remove script on unmount as it might be used by other components
		};
	}, [isConfigured, onError]);

	useEffect(() => {
		if (!onQuestionnaireCreated) return;

		const handleQuestionnaireCreated = (event: Event) => {
			const customEvent = event as CustomEvent<
				QuestionnaireCreatedEvent["detail"]
			>;
			onQuestionnaireCreated(customEvent.detail);
		};

		window.addEventListener(
			"onQuestionnaireCreated",
			handleQuestionnaireCreated,
		);

		return () => {
			window.removeEventListener(
				"onQuestionnaireCreated",
				handleQuestionnaireCreated,
			);
		};
	}, [onQuestionnaireCreated]);

	const openQuestionnaire = (payload: QuestionnairePayload) => {
		if (!window.slsIqApp) {
			const error = new Error("SDK not initialized");
			onError?.(error);
			return;
		}

		try {
			window.slsIqApp.openQuestionnaireForm(payload);
		} catch (error) {
			onError?.(error as Error);
		}
	};

	return {
		isConfigured,
		isLoaded,
		isLoading,
		openQuestionnaire,
	};
};
